/**
 * linkifyRichText — turns #hashtags and @mentions inside already-sanitized
 * post/comment HTML into clickable spans.
 *
 * Runs after `sanitizeHtml`, never before: the sanitizer strips `button`
 * elements and unknown attributes, so the markup added here would not
 * survive it. Only text nodes are rewritten, so existing links, embeds and
 * formatting are left untouched.
 */

import { UNRESOLVED_MENTION, resolveMention, type MentionIndex } from './mentions';

/** Same pattern the plain-text renderer uses, so both agree on what is a tag. */
const RICH_TEXT_PATTERN = /(@[a-zA-Z0-9_.]+)|(#[\p{L}\p{N}_]+)/gu;

/** Elements whose text must stay plain (already interactive or verbatim). */
const SKIPPED_ANCESTORS = new Set(['A', 'CODE', 'PRE', 'IFRAME']);

export const HASHTAG_ATTR = 'data-hashtag';
export const MENTION_ATTR = 'data-mention';
/** Identity resolved while linkifying; clicks read this, never the label. */
export const MENTION_ID_ATTR = 'data-mention-id';
/** Present when the label belongs to more than one known member. */
export const MENTION_AMBIGUOUS_ATTR = 'data-mention-ambiguous';

const hasSkippedAncestor = (node: Node, root: Node): boolean => {
  let current = node.parentNode;
  while (current && current !== root) {
    if (current.nodeType === Node.ELEMENT_NODE
      && SKIPPED_ANCESTORS.has((current as Element).tagName)) {
      return true;
    }
    current = current.parentNode;
  }
  return false;
};

const buildLink = (
  doc: Document,
  attribute: string,
  value: string,
  label: string,
  className: string,
): HTMLElement => {
  const span = doc.createElement('span');
  span.setAttribute(attribute, value);
  span.setAttribute('role', 'button');
  span.setAttribute('tabindex', '0');
  span.className = className;
  span.textContent = label;
  return span;
};

/** Replaces one text node with a mix of text and hashtag/mention spans. */
const linkifyTextNode = (
  doc: Document,
  textNode: Text,
  mentions?: MentionIndex,
): void => {
  const text = textNode.textContent ?? '';
  RICH_TEXT_PATTERN.lastIndex = 0;
  if (!RICH_TEXT_PATTERN.test(text)) return;

  RICH_TEXT_PATTERN.lastIndex = 0;
  const fragment = doc.createDocumentFragment();
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = RICH_TEXT_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      fragment.appendChild(doc.createTextNode(text.slice(lastIndex, match.index)));
    }
    if (match[1]) {
      const label = match[1].slice(1);
      const identity = mentions
        ? resolveMention(mentions, label)
        : UNRESOLVED_MENTION;
      // Only resolved, non-ambiguous mentions are interactive; others stay
      // as plain text so they never navigate to the wrong (or no) profile.
      if (identity.userGuid && !identity.ambiguous) {
        const displayLabel = identity.displayName
          ? `@${identity.displayName}`
          : match[1];
        const span = buildLink(doc, MENTION_ATTR, label, displayLabel, 'mention-link');
        span.setAttribute(MENTION_ID_ATTR, identity.userGuid);
        fragment.appendChild(span);
      } else {
        fragment.appendChild(doc.createTextNode(match[1]));
      }
    } else {
      fragment.appendChild(
        buildLink(doc, HASHTAG_ATTR, match[2].slice(1), match[2], 'hashtag-link'),
      );
    }
    lastIndex = RICH_TEXT_PATTERN.lastIndex;
  }

  if (lastIndex < text.length) {
    fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
  }
  textNode.parentNode?.replaceChild(fragment, textNode);
};

/**
 * Returns the HTML with hashtags and mentions wrapped in clickable spans.
 * `HtmlContent` handles the clicks by delegation using the data attributes.
 */
export function linkifyRichText(
  sanitizedHtml: string,
  mentions?: MentionIndex,
): string {
  if (!sanitizedHtml) return '';
  if (!/[@#]/.test(sanitizedHtml)) return sanitizedHtml;

  const doc = new DOMParser().parseFromString(sanitizedHtml, 'text/html');
  const body = doc.body;
  const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);

  const targets: Text[] = [];
  let node = walker.nextNode();
  while (node) {
    if (!hasSkippedAncestor(node, body)) targets.push(node as Text);
    node = walker.nextNode();
  }

  targets.forEach(textNode => linkifyTextNode(doc, textNode, mentions));
  return body.innerHTML;
}
